/*
 * <yardage-note> — live "what you'll receive" blurb for fabric-by-the-yard products.
 *
 * The blurb is server-rendered in snippets/yardage-note.liquid (so the correct state
 * paints with no JS and no layout shift). This element keeps it in sync when the
 * customer changes the variant or the quantity.
 *
 * Design notes for this Dawn-derived (FoxTheme) theme:
 *  - The blurb lives OUTSIDE <variant-selects>, so the theme's Section Rendering
 *    update (which only re-renders <variant-selects> + a few #id nodes) never
 *    destroys or duplicates it — connectedCallback runs once per instance.
 *  - Variant changes are read from FoxTheme's `variantChange` pubsub event, which
 *    fires AFTER the fetched variant HTML has landed and after the theme has reset
 *    the quantity to its minimum — so re-reading the quantity here is always current.
 *  - Everything is scoped to this element's section id; a page can hold several
 *    product forms (sticky ATC bar, quick view) without cross-talk.
 *
 * Copy + parsing rules are mirrored in snippets/yardage-note.liquid — keep in sync.
 */
if (!customElements.get('yardage-note')) {
  const YARDAGE_RE = /^(\d+)\s*yards?\b/i;
  const SWATCH_RE = /swatch/i;
  const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

  // Spell out one–ten, digits for 11+ (matches the copy spec).
  const toWord = (n) => (n >= 1 && n <= 10 ? NUMBER_WORDS[n] : String(n));

  const ICONS = {
    ruler:
      '<svg class="yardage-note__svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="1.5" y="8" width="21" height="8" rx="1.5"/><path d="M6 8v3M10 8v4M14 8v3M18 8v4"/></svg>',
    scissors:
      '<svg class="yardage-note__svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="2.6"/><circle cx="6" cy="18" r="2.6"/><path d="M8.4 8.4 20 20M20 4 8.4 15.6"/></svg>',
    grid:
      '<svg class="yardage-note__svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 3v18M3 12h18"/></svg>',
  };

  // Find the yardage/swatch descriptor by scanning the selected variant's option
  // values (position-independent, so no per-product config is needed).
  const parseSize = (variant) => {
    if (!variant) return null;

    const values = [];
    if (Array.isArray(variant.options)) values.push(...variant.options);
    if (typeof variant.title === 'string') values.push(...variant.title.split(' / '));

    for (const value of values) {
      if (typeof value !== 'string') continue;
      const match = value.match(YARDAGE_RE);
      if (match) {
        const yards = parseInt(match[1], 10);
        if (yards > 0) return { type: 'yardage', yards };
      }
    }

    for (const value of values) {
      if (typeof value === 'string' && SWATCH_RE.test(value)) return { type: 'swatch' };
    }

    return null;
  };

  customElements.define(
    'yardage-note',
    class YardageNote extends HTMLElement {
      constructor() {
        super();
        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onVariantChangedEvent = this.onVariantChangedEvent.bind(this);
      }

      connectedCallback() {
        this.box = this.querySelector('.yardage-note__box');
        this.iconEl = this.querySelector('.yardage-note__icon');
        this.textEl = this.querySelector('.yardage-note__text');
        if (!this.box || !this.textEl) return;

        this.sectionId = this.dataset.section;
        this.quantityInput = this.sectionId ? document.getElementById('Quantity-' + this.sectionId) : null;
        this.currentVariant = this.readVariantFromDOM();

        // Trust the server-rendered markup for the first paint. We only record which
        // state it represents so the first real change is diffed correctly — touching
        // no DOM here means no flash and no spurious screen-reader announcement.
        const initial = this.computeState();
        this.renderedKey = initial.hidden ? 'hidden' : initial.key;

        if (window.FoxTheme && FoxTheme.pubsub && typeof FoxTheme.pubsub.subscribe === 'function') {
          this.variantUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.onVariantChange
          );
        } else {
          // Fallback: product-info also dispatches this DOM event alongside the pubsub.
          document.addEventListener('variant:changed', this.onVariantChangedEvent);
        }

        if (this.quantityInput) {
          // `input` covers manual typing; `change` covers the +/- steppers and blur.
          this.quantityInput.addEventListener('input', this.onQuantityChange);
          this.quantityInput.addEventListener('change', this.onQuantityChange);
        }
      }

      disconnectedCallback() {
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        document.removeEventListener('variant:changed', this.onVariantChangedEvent);
        if (this.quantityInput) {
          this.quantityInput.removeEventListener('input', this.onQuantityChange);
          this.quantityInput.removeEventListener('change', this.onQuantityChange);
        }
        clearTimeout(this.quantityTimer);
      }

      onVariantChange(event) {
        const payload = event && event.data;
        if (!payload || payload.sectionId !== this.sectionId) return;
        this.currentVariant = payload.variant || this.readVariantFromDOM();
        this.render();
      }

      onVariantChangedEvent(event) {
        this.currentVariant = (event && event.detail && event.detail.variant) || this.readVariantFromDOM();
        this.render();
      }

      onQuantityChange() {
        clearTimeout(this.quantityTimer);
        this.quantityTimer = setTimeout(() => this.render(), 150);
      }

      render() {
        const state = this.computeState();

        if (state.hidden) {
          if (this.renderedKey !== 'hidden') {
            this.hidden = true;
            this.renderedKey = 'hidden';
          }
          return;
        }

        // Same state as what's already shown → do nothing (no flicker, no re-announce).
        if (state.key === this.renderedKey) return;

        this.hidden = false;
        if (this.box.dataset.state !== state.tint) this.box.dataset.state = state.tint;
        if (this.box.dataset.icon !== state.icon) {
          this.iconEl.innerHTML = ICONS[state.icon] || '';
          this.box.dataset.icon = state.icon;
        }
        // Updating the aria-live text node is what announces the change.
        this.textEl.innerHTML = state.html;
        this.renderedKey = state.key;
      }

      computeState() {
        const size = parseSize(this.currentVariant);
        if (!size) return { hidden: true };

        const qty = this.getQuantity();

        if (size.type === 'swatch') {
          const noun = qty === 1 ? 'swatch' : 'swatches';
          return {
            key: 'swatch:' + qty,
            tint: 'info',
            icon: 'grid',
            // Copy table leaves the swatch object phrase unbolded (unlike the yardage rows).
            html: 'You\'ll receive ' + toWord(qty) + ' 5" × 5" ' + noun + '.',
          };
        }

        const yards = size.yards;

        if (qty <= 1) {
          return {
            key: 'yardage:' + yards + ':1',
            tint: 'info',
            icon: 'ruler',
            html:
              'You\'ll receive <strong>one continuous ' +
              yards +
              '-yard piece</strong> — ' +
              yards * 3 +
              ' ft long × 54" wide.',
          };
        }

        const word = toWord(qty);
        return {
          key: 'yardage:' + yards + ':' + qty,
          tint: 'warn',
          icon: 'scissors',
          html:
            'You\'ll receive <strong>' +
            word +
            ' individual ' +
            yards +
            '-yard cuts</strong> — ' +
            word +
            ' separate pieces, not one continuous length. Need one continuous piece? Select your total yardage above.',
        };
      }

      getQuantity() {
        if (!this.quantityInput) return 1;
        let qty = parseInt(this.quantityInput.value, 10);
        // Empty / 0 / NaN read as 1 for display; never drop below the field's min.
        if (isNaN(qty) || qty < 1) qty = 1;
        const min = parseInt(this.quantityInput.getAttribute('min'), 10);
        if (!isNaN(min) && min > qty) qty = min;
        return qty;
      }

      readVariantFromDOM() {
        if (!this.sectionId) return null;
        const selects = document.getElementById('variant-selects-' + this.sectionId);
        const blob = selects && selects.querySelector('[data-selected-variant]');
        if (!blob) return null;
        try {
          return JSON.parse(blob.textContent);
        } catch (error) {
          return null;
        }
      }
    }
  );
}
